import { Button, ButtonVariants, LoaderTalos, Text, VStack } from '@talos/kyoko';
import { useRef } from 'react';
import type { PreTradeResponseData } from '../redux/preTradeApi';
import { useGetPreTradeDataQuery } from '../redux/preTradeApi';
import type { PreTradeRouteState, PreTradeTabType } from '../types';
import { PreTrade } from './PreTrade';

const POLL_INTERVAL_MS = 300;

interface PreTradeDataBoundaryProps {
  sessionId: string;
  isActiveSession: boolean;
  openTab: PreTradeRouteState['openTab'];
  requestId: string | undefined;
  onTabChange: (tab: PreTradeTabType, requestId?: string) => void;
}

export function PreTradeDataBoundary({
  sessionId,
  isActiveSession,
  openTab,
  requestId,
  onTabChange,
}: PreTradeDataBoundaryProps) {
  // Refs for pollingInterval
  // Polling stops once we have rows or after an error so failed requests aren't retried on the interval
  const dataRef = useRef<PreTradeResponseData[] | undefined>(undefined);
  const hasErrorRef = useRef(false);
  const { data, currentData, isError, refetch } = useGetPreTradeDataQuery(
    { SessionID: sessionId },
    { pollingInterval: dataRef.current?.length || hasErrorRef.current ? 0 : POLL_INTERVAL_MS }
  );
  dataRef.current = currentData;
  hasErrorRef.current = isError;

  // Loading until scenarios arrive.
  if (!data?.length && !isError) {
    return <LoaderTalos dataTestID="pre-trade-session-loading" />;
  }

  if (isError || !data) {
    return (
      <VStack h="100%" alignItems="center" justifyContent="center" gap="spacing8" data-testid="pre-trade-session-error">
        <Text color="colorTextSubtle">Unable to load pre-trade session.</Text>
        <Button
          variant={ButtonVariants.Default}
          onClick={() => {
            void refetch();
          }}
          data-testid="pre-trade-session-retry"
        >
          Retry
        </Button>
      </VStack>
    );
  }

  return (
    <PreTrade
      sessionId={sessionId}
      scenarios={data}
      isActiveSession={isActiveSession}
      openTab={openTab}
      requestId={requestId}
      onTabChange={onTabChange}
    />
  );
}
